import { motion } from 'framer-motion'
import { ArrowRight, Calendar, CheckCircle2 } from 'lucide-react'
import { TestOpsCommandLogo } from '@ytest/testops-command'
import { TerminalChrome, PromptLine, BlinkingCursor, TypingText } from './Terminal'

const YTEST_URL = import.meta.env.VITE_YTEST_URL || 'https://ytest.ai'

const SAMPLE_COMMANDS = [
  'show failing tests from last week and link related Jira tickets',
  'rerun the smoke suite against staging and post results to #qa',
  'file Jira bugs for every regression and assign to the right owner',
  'compare this sprint’s pass rate to last sprint and call out drops',
  'which tests are blocking the next release?',
]

export function Hero({ onScheduleDemo }: { onScheduleDemo: () => void }) {
  return (
    <section className="relative isolate overflow-hidden bg-zinc-950 pt-32 pb-24 sm:pt-40 sm:pb-32">
      {/* Ambient lighting — cyan-family. Two glows (was three) to keep GPU
       * load reasonable on lower-end machines; the gradient does the rest. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/4 h-[36rem] w-[36rem] rounded-full bg-cyan-500/10 blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[32rem] w-[32rem] rounded-full bg-sky-500/10 blur-[100px]" />
        {/* Faint grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            maskImage:
              'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 80%)',
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 backdrop-blur"
          >
            <TestOpsCommandLogo size={16} />
            <span className="font-mono text-xs text-white/70">
              ytest.ai &bull; flagship product
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-8 text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            Command your
            <br />
            <span className="bg-gradient-to-r from-cyan-400 to-sky-300 bg-clip-text text-transparent">
              testing org
            </span>
            <BlinkingCursor className="text-cyan-400" />
          </motion.h1>

          {/* Subhead */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/70 sm:text-xl"
          >
            One prompt across your test runs, tickets, code, and docs.
            Run, triage, and ship — in plain English, with full audit trail.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
          >
            <a
              href={`${YTEST_URL}/signup`}
              className="group inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-6 py-3.5 text-base font-semibold text-white shadow-2xl shadow-cyan-500/30 transition hover:-translate-y-0.5 hover:bg-cyan-600 hover:shadow-cyan-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            >
              Start free trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <button
              onClick={onScheduleDemo}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-6 py-3.5 text-base font-semibold text-white/90 backdrop-blur transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
            >
              <Calendar className="h-4 w-4 text-cyan-400" />
              Schedule a demo
            </button>
          </motion.div>

          {/* Trust line — value-first for the hero. Security plumbing
           * (BYOK / tenant isolation) lives in the Capabilities section
           * below. /60 opacity meets WCAG AA on dark. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/60"
          >
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
              End-to-end orchestration
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
              No context switching
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
              Audit + consent on every write
            </span>
          </motion.div>
        </div>

        {/* Live demo terminal */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="relative mx-auto mt-20 max-w-4xl"
        >
          {/* Glow behind terminal — cyan family. */}
          <div className="absolute -inset-x-8 -inset-y-4 rounded-3xl bg-gradient-to-r from-cyan-500/20 via-sky-500/20 to-blue-500/20 opacity-60 blur-2xl" />

          <TerminalChrome className="relative">
            <div className="space-y-3 px-6 py-6 font-mono text-sm">
              {/* Static intro lines */}
              <PromptLine prefix="$" prefixColor="text-white/30">
                <span className="text-white/50">testops --tenant=acme --connect</span>
              </PromptLine>
              <div className="font-mono text-xs text-emerald-300/80">
                &nbsp;&nbsp;&#10003; connected: jira, github, postgres, slack, jenkins{' '}
                <span className="text-white/30">+ 7 more</span>
              </div>
              <div className="border-t border-white/5 pt-3" />

              {/* Animated typing line */}
              <PromptLine prefix=">" prefixColor="text-cyan-400">
                <TypingText sequence={SAMPLE_COMMANDS} />
              </PromptLine>

              {/* Synthetic streaming response — represents what the agent does */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1.2 }}
                className="space-y-2 pl-5 pt-2 text-xs text-white/60"
              >
                <div>
                  <span className="text-cyan-400">▸</span> calling{' '}
                  <span className="text-sky-300">jira_search_issues</span>
                  <span className="text-white/30"> ... </span>
                  <span className="text-emerald-300">12 results</span>
                </div>
                <div>
                  <span className="text-cyan-400">▸</span> calling{' '}
                  <span className="text-sky-300">athena_get_executions</span>
                  <span className="text-white/30"> ... </span>
                  <span className="text-emerald-300">8 failed</span>
                </div>
                <div>
                  <span className="text-cyan-400">▸</span> calling{' '}
                  <span className="text-sky-300">github_search_code</span>
                  <span className="text-white/30"> ... </span>
                  <span className="text-emerald-300">3 commits</span>
                </div>
                <div className="pt-2 text-white/80">
                  Found <span className="text-cyan-300 font-semibold">8 failing tests</span> across the
                  last 7 days, all linked to{' '}
                  <span className="text-cyan-300 font-semibold">3 open Jira issues</span>{' '}
                  (KAN-7, KAN-12, KAN-18). Top suspect: a checkout regression introduced in
                  PR #284. Want me to file the missing tickets and rerun the suite?
                </div>
              </motion.div>
            </div>
          </TerminalChrome>
        </motion.div>
      </div>
    </section>
  )
}
